import i18n from "@/lang/lang.js";
import { customAlphabet } from "nanoid";
import { BigNumber } from "bignumber.js";
import { Decimal } from "decimal.js-light";
import { $log } from "@/plugins/logger/logger.plugin";

export const simpleValidationObject = (objectToBeValidated, mandatoryFields) =>
	functions.simpleValidationObject(objectToBeValidated, mandatoryFields);

export const sleep = (seconds) => {
	return new Promise((resolve) => setTimeout(resolve, seconds * 1000));
};

export const removeNullParamsFromObject = (obj) =>
	functions.removeNullParamsFromObject(obj);

export const generateRandomId = (charsToBeUsed = null, qttChars = 10) =>
	functions.generateRandomId(charsToBeUsed, qttChars);

export const generateRandomInteger = (min = 0, max = 1000) =>
	functions.generateRandomInteger(min, max);

export const appendZero = (str) => {
	str = str.toString();
	if (str.length === 1) {
		str = `0${str}`;
	}
	return str;
};

export const uppercaseFirstLetter = (word) =>
	functions.uppercaseFirstLetter(word);
export const humanizeWord = (word) => functions.humanizeWord(word);
export const splitCamelCaseWord = (word) => functions.splitCamelCaseWord(word);

export const parseJsonWithBigInt = (value) =>
	functions.parseJsonWithBigInt(value);

export const isObjectEmpty = (obj) => functions.isObjectEmpty(obj);

export const objectDiff = (object, base) => functions.objectDiff(object, base);

export const normalizeString = (str) => functions.normalizeString(str);

export const isNumber = (value) => functions.isNumber(value);

export const isBigInt = (value) => functions.isBigInt(value);

export const isBoolean = (value) => functions.isBoolean(value);

export const removeReactivityFromObject = (obj) =>
	functions.removeReactivityFromObject(obj);

// the implementation of this cache/memoization technique here is due to performance issue from i18n lib
const cacheHumanAccessType = new Map();
export const humanAccessType = (accessType) => {
	if (cacheHumanAccessType.has(accessType)) {
		return cacheHumanAccessType.get(accessType);
	}

	let finalValue;
	switch (accessType) {
		case "REMOTE_READ_ONLY":
			finalValue = i18n.t("read");
			break;
		case "REMOTE_WRITE_ONLY":
			finalValue = i18n.t("write");
			break;
		case "REMOTE_READ_WRITE":
			finalValue = i18n.t("read-and-write");
			break;
		default:
			finalValue = accessType;
			break;
	}

	cacheHumanAccessType.set(accessType, finalValue);
	return finalValue;
};

export const apifyAccessType = (accessType) => {
	let access;
	switch (accessType) {
		case "READ":
			access = "REMOTE_READ_ONLY";
			break;
		case "WRITE":
			access = "REMOTE_WRITE_ONLY";
			break;
		case "READ_WRITE":
			access = "REMOTE_READ_WRITE";
			break;
		case "LOCAL":
			access = accessType;
			break;
		default:
			throw new Error("Invalid type");
	}
	return access;
};

export const createArrayFromApiErrorResponse = (error, callbackMessage) =>
	functions.createArrayFromApiErrorResponse(error, callbackMessage);

export const roundFloatNumberToString = (value, digit = 2) =>
	functions.roundFloatNumberToString(value, digit);

/**
 * Download a file from blob
 *
 * @param {string} blob Blob to be added to file
 * @param {string} exportedFilename Filename that will be outputed to user
 */
export const downloadFileFromBlob = (blob, exportedFilename = "file.csv") => {
	if (navigator.msSaveBlob) {
		navigator.msSaveBlob(blob, exportedFilename);
	} else {
		const downloadLink = document.createElement("a");
		if (downloadLink.download !== undefined) {
			const url = URL.createObjectURL(blob);
			downloadLink.setAttribute("href", url);
			downloadLink.setAttribute("download", exportedFilename);
			downloadLink.style.visibility = "hidden";
			document.body.append(downloadLink);
			downloadLink.click();
			document.body.removeChild(downloadLink);
		}
	}
};

/**
 * Download a file
 *
 * @param {string} data Data to be added to file
 * @param {string} exportedFilename Filename that will be outputed to user
 * @param {string} type Type of file. Default CSV
 */
export const downloadFile = (
	data = "",
	exportedFilename = "file.csv",
	type = "text/csv;charset=utf-8;"
) => {
	let blob = new Blob([data], { type });

	downloadFileFromBlob(blob, exportedFilename);
};

function fallbackCopyToClipboard(txt) {
	const dummyTA = document.createElement("textarea");
	dummyTA.value = txt;

	// Avoid scrolling to bottom
	dummyTA.style.top = "0";
	dummyTA.style.left = "0";
	dummyTA.style.position = "fixed";

	document.body.append(dummyTA);
	dummyTA.focus();
	dummyTA.select();

	try {
		const successful = document.execCommand("copy");
		document.body.removeChild(dummyTA);
		return successful;
	} catch (err) {
		$log.error("Fallback: Oops, unable to copy", err);
		document.body.removeChild(dummyTA);
		return false;
	}
}

export const copyToClipboard = (text = "") => {
	if (!navigator.clipboard) {
		return fallbackCopyToClipboard(text);
	}
	return navigator.clipboard.writeText(text).then(
		function () {
			return true;
		},
		function (err) {
			$log.error("Could not copy text:", err);
			return false;
		}
	);
};

/**
 * Natural Sorting (http://www.davekoelle.com/files/alphanum.js)
 * This sort will solve the issue with string + number ([a2, a1, a10].sort() => [a1, a10, a2] || [a2, a1, a10].naturalSort() => [a1, a2, a10] )
 * @param {(array|string)} a
 * @param {(array|string)} b
 * @returns {number} Sorted array
 */
export function naturalSorting(a, b) {
	/* alphanum.js (C) Brian Huisman
	 * Based on the Alphanum Algorithm by David Koelle
	 * The Alphanum Algorithm is discussed at http://www.DaveKoelle.com
	 *
	 * Distributed under same license as original
	 *
	 * Released under the MIT License - https://opensource.org/licenses/MIT
	 *
	 * Permission is hereby granted, free of charge, to any person obtaining
	 * a copy of this software and associated documentation files (the "Software"),
	 * to deal in the Software without restriction, including without limitation
	 * the rights to use, copy, modify, merge, publish, distribute, sublicense,
	 * and/or sell copies of the Software, and to permit persons to whom the
	 * Software is furnished to do so, subject to the following conditions:
	 *
	 * The above copyright notice and this permission notice shall be included
	 * in all copies or substantial portions of the Software.
	 *
	 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
	 * EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
	 * MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
	 * IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
	 * DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
	 * OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE
	 * USE OR OTHER DEALINGS IN THE SOFTWARE.
	 */
	function chunkify(t) {
		let tz = [];
		let x = 0,
			y = -1,
			n = 0,
			i,
			j;

		while ((i = (j = t.charAt(x++)).codePointAt(0))) {
			let m = i == 46 || (i >= 48 && i <= 57);
			if (m !== n) {
				tz[++y] = "";
				n = m;
			}
			tz[y] += j;
		}
		return tz;
	}

	let aa = chunkify((a || "").toString().toLowerCase());
	let bb = chunkify((b || "").toString().toLowerCase());

	for (let x = 0; aa[x] && bb[x]; x++) {
		if (aa[x] !== bb[x]) {
			let c = Number(aa[x]),
				d = Number(bb[x]);
			if (c == aa[x] && d == bb[x]) {
				return c - d;
			}
			return aa[x] > bb[x] ? 1 : -1;
		}
	}
	return aa.length - bb.length;
}

/**
 * Takes an Array<V>, and a grouping function,
 * and returns a Map of the array grouped by the grouping function.
 *
 * @param list An array of type V.
 * @param keyGetter A Function that takes the the Array type V as an input, and returns a value of type K.
 *                  K is generally intended to be a property key of V.
 *
 * @returns Map of the array grouped by the grouping function.
 */
export function groupBy(list, keyGetter) {
	const map = new Map();
	list.forEach((item) => {
		const key = keyGetter(item);
		const collection = map.get(key);
		if (!collection) {
			map.set(key, [item]);
		} else {
			collection.push(item);
		}
	});
	return map;
}

export const isDeviceUplinkActive = (deviceUplink) =>
	functions.isDeviceUplinkActive(deviceUplink);

export const humanFriendlyStatus = (status) =>
	functions.humanFriendlyStatus(status);

const functions = {
	splitCamelCaseWord(word) {
		return word.replace(/([A-Z]+)/g, " $1");
	},

	uppercaseFirstLetter(str) {
		return str.slice(0, 1).toUpperCase() + str.slice(1);
	},

	humanizeWord(word) {
		return this.uppercaseFirstLetter(this.splitCamelCaseWord(word));
	},

	removeNullParamsFromObject(obj) {
		const cloneConfig = this.removeReactivityFromObject(obj);

		Object.keys(cloneConfig).forEach((key) => {
			if (cloneConfig[key] && typeof cloneConfig[key] === "object") {
				cloneConfig[key] = this.removeNullParamsFromObject(
					cloneConfig[key]
				);
				// if object is empty, remove it too
				if (Object.keys(cloneConfig[key]).length === 0) {
					delete cloneConfig[key];
				}
			} else if (cloneConfig[key] == null) {
				delete cloneConfig[key];
			}
		});

		return cloneConfig;
	},

	// https://www.measurethat.net/Benchmarks/Show/3291/0/object-deep-copy
	recurisveDeepClone(obj) {
		return Object.keys(obj).reduce(
			(v, d) =>
				Object.assign(v, {
					[d]:
						obj[d]?.constructor === Object
							? this.recurisveDeepClone(obj[d])
							: obj[d]
				}),
			{}
		);
	},

	removeReactivityFromObject(obj) {
		if (typeof obj === "object" && !Array.isArray(obj) && obj !== null) {
			try {
				// found this script on this benchmark and looks like it's way more performant than the JSON.parse(JSON.stringify())
				// https://www.measurethat.net/Benchmarks/Show/3291/0/object-deep-copy
				return this.recurisveDeepClone(obj);
			} catch {
				// if that script fails, we fallback to traditional way
				return JSON.parse(JSON.stringify(obj));
			}
		} else {
			return JSON.parse(JSON.stringify(obj));
		}
	},

	sleep(seconds) {
		return sleep(seconds);
	},

	replaceSpecialCharacteres(term) {
		if (!term) {
			return "";
		}
		const charsToBeReplaced =
			"àáâäãåāèéêëēėęîïíīįìôöòóøōõûüùúūñńçćčßÿœæŕšśńṕẃǵǹḿǘẍźḧ";
		const charsWillReplace =
			"aaaaaaaeeeeeeeiiiiiiooooooouuuuunncccsyoarssnpwgnmuxzh";
		const regex = new RegExp(charsToBeReplaced.split("").join("|"), "g");

		return term
			.toString()
			.toLowerCase()
			.replace(regex, (_char) =>
				charsWillReplace.charAt(charsToBeReplaced.indexOf(_char))
			);
	},

	simpleValidationObject(objectToBeValidated, mandatoryFields) {
		if (!mandatoryFields || !Array.isArray(mandatoryFields)) {
			throw new Error("You must provide a mandatoryFields array");
		}

		for (let fieldName of mandatoryFields) {
			const fieldValue = objectToBeValidated[fieldName];
			const field = this.humanizeWord(fieldName);

			if (
				fieldValue === null ||
				fieldValue === undefined ||
				fieldValue.toString().trim() === ""
			) {
				throw new TypeError(
					i18n.t("field-is-required", {
						field
					})
				);
			}
		}

		return true;
	},

	isNumber(value) {
		if (
			typeof value === "object" &&
			Object.prototype.toString.apply(value) === "[object Array]"
		) {
			return false;
		}
		if (typeof value === "boolean") {
			return false;
		}
		if (typeof value === "bigint") {
			return false;
		}

		try {
			const num = Number(value);
			// const valueAsBigInt = Number.isInteger(value)
			// 	? BigInt(value)
			// 	: Number(value);
			const valueAsBigInt = Number.isInteger(value)
				? Number(BigInt(value))
				: Number(value);
			return (
				value !== null &&
				typeof num === "number" &&
				!isNaN(num) &&
				num === valueAsBigInt
			);
		} catch {
			return false;
		}
	},

	isBigInt(value) {
		if (
			typeof value === "object" &&
			Object.prototype.toString.apply(value) === "[object Array]"
		) {
			return false;
		}

		if (typeof value === "boolean") {
			return false;
		}

		if (typeof value === "bigint") {
			return true;
		}

		try {
			const valueAsBigInt = BigInt(value);
			const valueAsNum = Number(value);
			return value !== null && valueAsBigInt != valueAsNum;
		} catch {
			return false;
		}
	},

	/**
	 * @description Round float number with X decimals. If not a float, return the same input
	 * @param {*} value
	 * @param {integer} [digit=2] default digit=2
	 * @returns {*} Return String if float value
	 */
	roundFloatNumberToString(value, digit = 2) {
		if (
			(typeof value === "number" &&
				!Number.isNaN(value) &&
				!Number.isInteger(value)) ||
			value instanceof Decimal
		) {
			return value.toFixed(digit);
		}

		if (value instanceof BigNumber) {
			try {
				// if BigInt (isInteger) => display without decimals as a string
				return value.toFixed(value.isInteger() ? 0 : digit);
			} catch {
				return value;
			}
		}
		return value;
	},

	isBoolean(obj) {
		return Object.prototype.toString.apply(obj) === "[object Boolean]";
	},

	generateRandomId(charsToBeUsed = null, qttChars = 10) {
		const chars = charsToBeUsed || "abcdefghijklmnopqrstuvwxyz123456789";
		return customAlphabet(chars, qttChars)();
	},

	// use BigInt to parse value over MAX_SAVE_INTEGER Number
	parseJsonWithBigInt(valueToParse) {
		// as this method is called recursively on the json, some objects might be null or undefined
		if (!valueToParse) {
			return valueToParse;
		}

		const result = JSON.parse(valueToParse, (key, value, context) => {
			// avoid reaching to Decimal() which would throw an exception and slow down parsing
			if (!value || typeof value === "object") {
				return value;
			}
			try {
				const valueAsDecimal = new Decimal(value);
				if (valueAsDecimal.isInteger()) {
					if (Number.isSafeInteger(Number(value))) {
						return Number(value);
					}

					return BigInt(context.source);
				}

				return new Decimal(context.source);
			} catch {
				return value;
			}
		});

		return result;
	},

	generateRandomInteger(min, max) {
		return Math.floor(Math.random() * (max - min + 1) + min);
	},

	isObjectEmpty(obj) {
		return (
			obj && Object.keys(obj).length === 0 && obj.constructor === Object
		);
	},

	objectDiff(object = {}, base = {}) {
		function isArray(obj) {
			return Object.prototype.toString.apply(obj) === "[object Array]";
		}

		function isObject(obj) {
			return Object.prototype.toString.apply(obj) === "[object Object]";
		}

		function isValue(obj) {
			return !isObject(obj) && !isArray(obj);
		}

		function sameValue(value1, value2) {
			return value1 === value2;
		}

		function areObjectsDifferent(obj1, obj2) {
			if (isArray(obj1) && isArray(obj2)) {
				return !sameValue(
					JSON.stringify([...obj1].toSorted()),
					JSON.stringify([...obj2].toSorted())
				);
			}

			if (isValue(obj1) && isValue(obj2)) {
				return !sameValue(obj1, obj2);
			}

			for (const key in obj1) {
				let value2;
				if (obj2[key] !== undefined) {
					value2 = obj2[key];
				}

				if (areObjectsDifferent(obj1[key], value2)) {
					return true;
				}
			}

			for (const key in obj2) {
				// If there's the same object in obj1, keep looping as obj1[key] == obj2[key]
				if (obj1[key] !== undefined) {
					continue;
				}
				// If reach this point, means obj2 has different keys than obj1
				return true; // ✓ Ajout du return true ici
			}

			return false; // ✓ Maintenant correctement placé
		}

		// if objects are identical, return null
		if (!areObjectsDifferent(object, base)) {
			return null; // equal
		}

		let finalObj = {};

		// merge all keys from object and base
		const allKeys = [
			...new Set([...Object.keys(object), ...Object.keys(base)])
		];

		for (const key of allKeys) {
			// If base object doesn't have the new object, add it
			if (!Object.prototype.hasOwnProperty.call(base, key)) {
				finalObj[key] = object[key];
			} else {
				// if object doesn't have the base[key] object, add it as empty
				if (!Object.prototype.hasOwnProperty.call(object, key)) {
					if (isArray(base[key])) {
						finalObj[key] = [];
					} else if (isObject(base[key])) {
						finalObj[key] = {};
					} else if (functions.isBoolean(base[key])) {
						finalObj[key] = base[key];
					} else if (isValue(base[key])) {
						finalObj[key] = "";
					}
					continue;
				}

				// if objects are the same, don't add to the finalObj
				if (!areObjectsDifferent(object[key], base[key])) {
					continue;
				}

				if (isArray(object[key])) {
					finalObj[key] = Object.assign([], object[key]);
				} else if (isValue(object[key])) {
					if (!sameValue(object[key], base[key])) {
						finalObj[key] = object[key];
					}
				} else if (isObject(object[key])) {
					const diff = this.objectDiff(object[key], base[key]);
					finalObj[key] = diff;
				}
			}
		}

		return finalObj;
	},

	normalizeString(str = "") {
		return str.normalize("NFD").replace(/[\u0300-\u036F]/g, "");
	},

	createArrayFromApiErrorResponse(error, callbackMessage = null) {
		let final = [];
		const errors = error?.data?.errors;
		if (errors) {
			for (const [key, value] of Object.entries(errors || {})) {
				if (Array.isArray(value)) {
					value.forEach((_value) => {
						final.push(`${key}: ${_value.toString()}`);
					});
				} else if (typeof value === "object") {
					for (const [_key, _value] of Object.entries(value)) {
						final.push(`${key} - ${_key}: ${_value.toString()}`);
					}
				} else {
					final.push(`${key}: ${value.toString()}`);
				}
			}
		} else {
			final.push(
				error?.data?.message || callbackMessage || i18n.t("failed")
			);
		}

		return final;
	},

	isDeviceUplinkActive(deviceUplink) {
		const { status = null, endAt = null } = deviceUplink || {};
		if (!status && !endAt) {
			return false;
		}
		if (status === "DATA_STOPPED") {
			return false;
		}
		if (status === "DATA_STARTED") {
			// wattsense user set UPLINK_DATA_NO_LIMIT, so it will be on "forever"
			if (endAt === 0) {
				return true;
			}
			const now = Date.now();
			return now < endAt;
		}
		$log.error(
			"deviceUplink status is different from DATA_STARTED and DATA_STOPPED",
			deviceUplink
		);
		return false;
	},

	humanFriendlyStatus(status) {
		switch (status) {
			case "ONLINE":
				return i18n.t("online");
			case "OFFLINE":
				return i18n.t("offline");
			case "NEVER_CONNECTED":
				return i18n.t("never-connected");
			default:
				return status;
		}
	}
};

export default functions;
